import React from "react";
import { FaArrowRight, FaBoxOpen, FaPlus, FaTags } from "react-icons/fa";
import { IoCartSharp } from "react-icons/io5";
import { MdDashboardCustomize, MdStar } from "react-icons/md";
import { RiCoupon3Fill } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useCategoriesQuery } from "../../service/categoryApi";
import { useCouponsQuery } from "../../service/couponApi";
import { useFeaturedsQuery } from "../../service/featuredApi";
import { useOrdersQuery } from "../../service/orderApi";
import { useProductsQuery } from "../../service/productApi";

const len = (d) => (Array.isArray(d?.data) ? d.data.length : Array.isArray(d) ? d.length : 0);
const initials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const StatCard = ({ icon, label, value, to, gradient }) => (
  <Link
    to={to}
    className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-gray-700/60 bg-gray-800 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
  >
    <div className="flex items-start justify-between">
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
        {icon}
      </div>
      <FaArrowRight className="h-4 w-4 -translate-x-1 text-gray-500 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />
    </div>
    <div className="mt-6">
      <p className="text-3xl font-bold tabular-nums text-white">{value ?? "—"}</p>
      <p className="mt-1 text-sm text-gray-400">{label}</p>
    </div>
  </Link>
);

const Home = () => {
  const { data: products } = useProductsQuery({ cat: "all", page: 1, limit: 1 });
  const { data: orders, isLoading: ordersLoading } = useOrdersQuery({ page: 1 });
  const { data: categories } = useCategoriesQuery();
  const { data: coupons } = useCouponsQuery();
  const { data: featured } = useFeaturedsQuery();

  const recent = orders?.data || [];
  const statusLabel = (s) =>
    s === "succeeded" ? "Paid" : s === "shipped" ? "Shipped" : s === "canceled" ? "Canceled" : s || "—";
  const pill = (s) =>
    s === "shipped"
      ? "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30"
      : s === "canceled"
      ? "bg-red-500/15 text-red-400 ring-red-500/30"
      : "bg-amber-500/15 text-amber-400 ring-amber-500/30";

  const breakdown = [
    { key: "succeeded", label: "Paid", dot: "bg-amber-400", bar: "bg-amber-400" },
    { key: "shipped", label: "Shipped", dot: "bg-emerald-400", bar: "bg-emerald-400" },
    { key: "canceled", label: "Canceled", dot: "bg-red-400", bar: "bg-red-400" },
  ].map((b) => ({ ...b, count: recent.filter((o) => o?.status === b.key).length }));
  const recentTotal = recent.length || 1;

  const stats = [
    { icon: <FaBoxOpen className="h-6 w-6 text-white" />, label: "Products", value: products?.pagination?.totalItems, to: "/dashboard/manage-products", gradient: "from-blue-500 to-blue-700" },
    { icon: <IoCartSharp className="h-6 w-6 text-white" />, label: "Orders", value: orders?.totalItems, to: "/dashboard/manage-orders", gradient: "from-violet-500 to-purple-700" },
    { icon: <FaTags className="h-6 w-6 text-white" />, label: "Categories", value: len(categories), to: "/dashboard/manage-categories", gradient: "from-emerald-500 to-teal-700" },
    { icon: <MdStar className="h-6 w-6 text-white" />, label: "Featured", value: len(featured), to: "/dashboard/manage-featureds", gradient: "from-amber-500 to-orange-700" },
    { icon: <RiCoupon3Fill className="h-6 w-6 text-white" />, label: "Coupons", value: len(coupons), to: "/dashboard/manage-coupons", gradient: "from-pink-500 to-rose-700" },
  ];

  const quickActions = [
    { label: "Add Product", to: "/dashboard/manage-products", icon: <FaBoxOpen className="h-5 w-5" /> },
    { label: "View Orders", to: "/dashboard/manage-orders", icon: <IoCartSharp className="h-5 w-5" /> },
    { label: "Categories", to: "/dashboard/manage-categories", icon: <MdDashboardCustomize className="h-5 w-5" /> },
    { label: "Coupons", to: "/dashboard/manage-coupons", icon: <RiCoupon3Fill className="h-5 w-5" /> },
  ];

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-400">Overview of your store at a glance.</p>
        </div>
        <Link
          to="/dashboard/manage-products"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 font-medium text-white shadow-lg shadow-blue-900/30 transition hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 motion-reduce:transition-none"
        >
          <FaPlus className="h-4 w-4" /> Add Product
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* Lower grid */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent orders */}
        <div className="rounded-2xl border border-gray-700/60 bg-gray-800 p-4 sm:p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Recent Orders</h2>
            <Link to="/dashboard/manage-orders" className="text-sm font-medium text-blue-400 transition hover:text-blue-300">
              View all
            </Link>
          </div>

          {ordersLoading ? (
            <p className="py-12 text-center text-gray-400">Loading orders…</p>
          ) : recent.length === 0 ? (
            <div className="py-12 text-center">
              <IoCartSharp className="mx-auto h-10 w-10 text-gray-600" />
              <p className="mt-2 text-gray-400">No orders yet.</p>
            </div>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="pb-3 pr-4 font-medium">Customer</th>
                    <th className="pb-3 pr-4 font-medium">Transaction</th>
                    <th className="pb-3 pr-4 font-medium">Total</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.slice(0, 8).map((o) => (
                    <tr key={o?._id} className="border-t border-gray-700/60 transition hover:bg-gray-700/30">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-700 text-xs font-semibold text-gray-200">
                            {initials(o?.shipping?.name)}
                          </span>
                          <span className="font-medium text-white">{o?.shipping?.name || "—"}</span>
                        </div>
                      </td>
                      <td className="max-w-[160px] truncate py-3 pr-4 text-gray-400">{o?.txId || "—"}</td>
                      <td className="py-3 pr-4 font-medium tabular-nums text-white">£{parseFloat(o?.amount).toFixed(2)}</td>
                      <td className="py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${pill(o?.status)}`}>
                          {statusLabel(o?.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className="flex flex-col gap-6">
          {/* Order status breakdown */}
          <div className="rounded-2xl border border-gray-700/60 bg-gray-800 p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Recent Order Status</h2>
            <div className="space-y-4">
              {breakdown.map((b) => (
                <div key={b.key}>
                  <div className="mb-1.5 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-300">
                      <span className={`h-2.5 w-2.5 rounded-full ${b.dot}`} />
                      {b.label}
                    </span>
                    <span className="font-semibold tabular-nums text-white">{b.count}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-700">
                    <div className={`h-full rounded-full ${b.bar} transition-all duration-500`} style={{ width: `${(b.count / recentTotal) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick actions */}
          <div className="rounded-2xl border border-gray-700/60 bg-gray-800 p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((a) => (
                <Link
                  key={a.label}
                  to={a.to}
                  className="flex flex-col items-center gap-2 rounded-xl border border-gray-700/60 bg-gray-900/40 p-4 text-center text-gray-300 transition hover:border-gray-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
                >
                  {a.icon}
                  <span className="text-xs font-medium">{a.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
